import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency, formatDate } from "@/lib/utils-format";
import { Plus, Printer, CheckCircle, XCircle, FileText, Eye } from "@phosphor-icons/react";

const INVOICE_STATUSES = {
  issued: { label: "Issued", cls: "bg-blue-50 text-blue-700 border-blue-300" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  void: { label: "Void", cls: "bg-zinc-100 text-zinc-500 border-zinc-300 line-through" },
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openView, setOpenView] = useState(null);

  const load = async () => setInvoices((await api.get("/invoices")).data);
  useEffect(() => { load(); }, []);

  return (
    <div className="p-8" data-testid="invoices-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Billing</p>
          <h1 className="text-4xl font-black tracking-tighter">Invoices</h1>
          <p className="text-sm text-zinc-500 mt-2">Monthly customer invoicing · INV-YYYY-NNNNNN · Net (no VAT)</p>
        </div>
        <Button onClick={() => setOpenNew(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-invoice-button">
          <Plus size={16} weight="bold" className="mr-1" /> New Invoice
        </Button>
      </div>

      <div className="border border-zinc-200 bg-white">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Invoice #</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Period</th>
              <th className="text-right px-4 py-2.5">Lines</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const s = INVOICE_STATUSES[inv.status] || INVOICE_STATUSES.issued;
              return (
                <tr key={inv.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`invoice-row-${inv.invoice_id}`}>
                  <td className="px-4 py-3 font-mono text-sm font-semibold">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-sm">{formatDate(inv.date_issued)}</td>
                  <td className="px-4 py-3 text-sm">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{formatDate(inv.date_from)} → {formatDate(inv.date_to)}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm">{inv.line_items?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-semibold">{formatCurrency(inv.total, inv.currency)}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[11px] font-medium border rounded ${s.cls}`}>{s.label}</span></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setOpenView(inv)} className="p-1 hover:bg-zinc-200 rounded" title="View" data-testid={`view-invoice-${inv.invoice_id}`}>
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {invoices.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">No invoices yet. Click "New Invoice" to generate one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <NewInvoiceDialog open={openNew} onClose={() => setOpenNew(false)} onDone={() => { setOpenNew(false); load(); }} />
      {openView && <ViewInvoiceDialog invoice={openView} onClose={() => setOpenView(null)} onChange={load} />}
    </div>
  );
}

function NewInvoiceDialog({ open, onClose, onDone }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "", customer_name: "", customer_email: "", customer_address: "",
    date_from: "", date_to: "", notes: "", discount: 0,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/customers").then((r) => setCustomers(r.data));
  }, [open]);

  const runPreview = async () => {
    if (!form.customer_name || !form.date_from || !form.date_to) {
      toast.error("Select customer and date range first");
      return;
    }
    try {
      const r = await api.get("/invoices/preview", {
        params: {
          customer_id: form.customer_id || undefined,
          customer_name: form.customer_name,
          date_from: form.date_from,
          date_to: form.date_to,
        },
      });
      setPreview(r.data);
    } catch (error) { toast.error(error.response?.data?.detail || "Preview failed"); }
  };

  const submit = async () => {
    if (!preview || preview.count === 0) { toast.error("No line items — run preview first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/invoices", form);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      onDone();
      setForm({ customer_id: "", customer_name: "", customer_email: "", customer_address: "", date_from: "", date_to: "", notes: "", discount: 0 });
      setPreview(null);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.customer_id === id);
    setForm({
      ...form,
      customer_id: c?.customer_id || "",
      customer_name: c?.company || c?.name || "",
      customer_email: c?.email || "",
      customer_address: c?.address || "",
    });
    setPreview(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto" data-testid="new-invoice-dialog">
        <DialogHeader><DialogTitle>Generate Invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Customer</Label>
              <select
                value={form.customer_id}
                onChange={(e) => pickCustomer(e.target.value)}
                className="w-full h-10 border border-zinc-300 rounded-md px-3 text-sm mt-1"
                data-testid="invoice-customer-select"
              >
                <option value="">-- Select customer --</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>{c.company || c.name} ({c.email || c.phone || "—"})</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Or enter customer name manually</Label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-customer-name-input" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Date From</Label>
              <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-date-from" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Date To</Label>
              <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-date-to" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Customer Email (optional)</Label>
              <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-customer-email" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Discount (optional)</Label>
              <Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-discount" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Billing Address (optional)</Label>
              <Textarea value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} rows={2}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-customer-address" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                className="border-zinc-300 rounded-md mt-1" data-testid="invoice-notes" />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={runPreview} className="border-zinc-300 rounded-md" data-testid="invoice-preview-button">
              <Eye size={14} weight="bold" className="mr-1" /> Preview Line Items
            </Button>
          </div>

          {preview && (
            <div className="border border-zinc-200 bg-zinc-50 p-4" data-testid="invoice-preview">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">{preview.count} line items · Subtotal {formatCurrency(preview.subtotal)}</p>
                <p className="text-xs text-zinc-500">After discount: <strong>{formatCurrency(preview.subtotal - (form.discount || 0))}</strong></p>
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                <table className="w-full data-table">
                  <thead className="sticky top-0 bg-zinc-100"><tr><th className="text-left px-2 py-1">Order</th><th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Product</th><th className="text-left px-2 py-1">Tracking</th><th className="text-right px-2 py-1">Total</th></tr></thead>
                  <tbody>
                    {preview.line_items.map((li) => (
                      <tr key={li.order_id} className="border-b border-zinc-100"><td className="px-2 py-1 font-mono text-xs">{li.order_number}</td><td className="px-2 py-1 text-xs">{li.date}</td><td className="px-2 py-1 text-xs">{li.product}</td><td className="px-2 py-1 font-mono text-xs">{li.tracking_number || "—"}</td><td className="px-2 py-1 text-right font-mono text-xs">{formatCurrency(li.total_amount, li.currency)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <Button variant="outline" onClick={onClose} className="border-zinc-300 rounded-md">Cancel</Button>
            <Button onClick={submit} disabled={busy || !preview || preview.count === 0} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="submit-invoice-button">
              <FileText size={14} weight="bold" className="mr-1" /> Generate Invoice
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewInvoiceDialog({ invoice, onClose, onChange }) {
  const [ref, setRef] = useState("");

  const markPaid = async () => {
    try {
      await api.post(`/invoices/${invoice.invoice_id}/mark-paid`, { payment_reference: ref });
      toast.success("Marked as paid");
      onChange();
      onClose();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const voidInvoice = async () => {
    if (!window.confirm("Void this invoice? Line items will be un-linked.")) return;
    try {
      await api.delete(`/invoices/${invoice.invoice_id}`);
      toast.success("Invoice voided");
      onChange();
      onClose();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const printMe = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const doc = win.document;
    doc.title = invoice.invoice_number;

    // Build via DOM APIs so no user-controlled string is parsed as HTML (XSS-safe).
    const style = doc.createElement("style");
    style.textContent = `body{font-family:Arial,sans-serif;padding:32px;color:#09090B;max-width:800px;margin:0 auto}
      h1{margin:0 0 8px;letter-spacing:-0.02em;font-size:32px}
      .head{display:flex;justify-content:space-between;margin-bottom:32px;align-items:flex-start}
      .meta{font-size:12px;line-height:1.5}
      .label{text-transform:uppercase;letter-spacing:0.15em;font-size:9px;color:#71717A;margin-bottom:2px}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-top:16px}
      th,td{border-bottom:1px solid #D4D4D8;padding:8px;text-align:left}
      th{background:#F4F4F5;text-transform:uppercase;letter-spacing:0.1em;font-size:9px}
      .totals{margin-top:16px;text-align:right;font-size:14px;padding-top:16px;border-top:2px solid #09090B}
      .total-final{font-size:24px;font-weight:900;margin-top:8px}`;
    doc.head.appendChild(style);

    const el = (tag, opts = {}, children = []) => {
      const n = doc.createElement(tag);
      if (opts.className) n.className = opts.className;
      if (opts.style) n.setAttribute("style", opts.style);
      if (opts.text != null) n.textContent = String(opts.text);
      children.forEach((c) => c && n.appendChild(c));
      return n;
    };
    const nl2br = (parent, text) => {
      (text || "").split("\n").forEach((line, i) => {
        if (i > 0) parent.appendChild(doc.createElement("br"));
        parent.appendChild(doc.createTextNode(line));
      });
    };

    const body = doc.body;
    const head = el("div", { className: "head" });

    const left = el("div");
    left.appendChild(el("h1", { text: "Invoice" }));
    const leftMeta = el("div", { className: "meta" });
    leftMeta.appendChild(el("strong", { text: invoice.invoice_number }));
    leftMeta.appendChild(doc.createElement("br"));
    leftMeta.appendChild(doc.createTextNode(`Issued: ${invoice.date_issued}`));
    leftMeta.appendChild(doc.createElement("br"));
    leftMeta.appendChild(doc.createTextNode(`Period: ${invoice.date_from} → ${invoice.date_to}`));
    left.appendChild(leftMeta);
    head.appendChild(left);

    const right = el("div", { className: "meta" });
    right.appendChild(el("div", { className: "label", text: "Bill To" }));
    right.appendChild(el("strong", { text: invoice.customer_name }));
    right.appendChild(doc.createElement("br"));
    nl2br(right, invoice.customer_address);
    right.appendChild(doc.createElement("br"));
    right.appendChild(doc.createTextNode(invoice.customer_email || ""));
    head.appendChild(right);

    body.appendChild(head);

    const table = el("table");
    const thead = el("thead");
    const trh = el("tr");
    ["Order", "Date", "Product", "Tracking", "Amount"].forEach((h, i) =>
      trh.appendChild(el("th", { text: h, style: i === 4 ? "text-align:right" : "" }))
    );
    thead.appendChild(trh);
    table.appendChild(thead);
    const tbody = el("tbody");
    (invoice.line_items || []).forEach((li) => {
      const tr = el("tr");
      tr.appendChild(el("td", { text: li.order_number }));
      tr.appendChild(el("td", { text: li.date }));
      tr.appendChild(el("td", { text: li.product }));
      tr.appendChild(el("td", { text: li.tracking_number || "" }));
      tr.appendChild(el("td", { text: `${li.total_amount.toFixed(2)} ${li.currency}`, style: "text-align:right" }));
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    body.appendChild(table);

    const totals = el("div", { className: "totals" });
    totals.appendChild(doc.createTextNode(`Subtotal: ${invoice.subtotal.toFixed(2)} ${invoice.currency}`));
    if (invoice.discount) {
      totals.appendChild(doc.createElement("br"));
      totals.appendChild(doc.createTextNode(`Discount: −${invoice.discount.toFixed(2)}`));
    }
    const finalTotal = el("div", { className: "total-final", text: `Total: ${invoice.total.toFixed(2)} ${invoice.currency}` });
    totals.appendChild(finalTotal);
    body.appendChild(totals);

    if (invoice.notes) {
      const notes = el("p", { style: "margin-top:24px;font-size:11px;color:#71717A" });
      notes.appendChild(el("strong", { text: "Notes: " }));
      notes.appendChild(doc.createTextNode(invoice.notes));
      body.appendChild(notes);
    }

    win.print();
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="view-invoice-dialog">
        <DialogHeader><DialogTitle>{invoice.invoice_number} · {invoice.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Issued" value={formatDate(invoice.date_issued)} />
            <Stat label="Period" value={`${formatDate(invoice.date_from)} → ${formatDate(invoice.date_to)}`} />
            <Stat label="Lines" value={invoice.line_items.length} />
            <Stat label="Total" value={formatCurrency(invoice.total, invoice.currency)} big />
          </div>
          <div className="border border-zinc-200 max-h-[280px] overflow-y-auto">
            <table className="w-full data-table">
              <thead className="sticky top-0 bg-zinc-50"><tr><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Product</th><th className="text-right px-3 py-2">Amount</th></tr></thead>
              <tbody>
                {invoice.line_items.map((li) => (
                  <tr key={li.order_id} className="border-b border-zinc-100"><td className="px-3 py-2 font-mono text-xs">{li.order_number}</td><td className="px-3 py-2 text-xs">{li.date}</td><td className="px-3 py-2 text-xs">{li.product}</td><td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(li.total_amount, li.currency)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {invoice.status === "issued" && (
            <div className="border border-zinc-200 p-3 bg-zinc-50">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Payment Reference (optional)</Label>
              <Input value={ref} onChange={(e) => setRef(e.target.value)} className="border-zinc-300 rounded-md mt-1" placeholder="Bank transfer ID or similar" data-testid="invoice-mark-paid-ref" />
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-zinc-200">
            {invoice.status === "issued" && (
              <Button variant="outline" onClick={voidInvoice} className="border-red-300 text-red-700 hover:bg-red-50 rounded-md" data-testid="void-invoice-button">
                <XCircle size={14} weight="bold" className="mr-1" /> Void
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={printMe} className="border-zinc-300 rounded-md" data-testid="print-invoice-button">
                <Printer size={14} weight="bold" className="mr-1" /> Print
              </Button>
              {invoice.status === "issued" && (
                <Button onClick={markPaid} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md" data-testid="mark-paid-button">
                  <CheckCircle size={14} weight="bold" className="mr-1" /> Mark Paid
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, big }) {
  return (
    <div className="border border-zinc-200 p-3 bg-zinc-50">
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</p>
      <p className={`font-black tracking-tighter font-mono mt-1 ${big ? "text-2xl" : "text-sm"}`}>{value}</p>
    </div>
  );
}
